/**
 * Compliance-document (manual + Form ADV Part 2A brochure) data access
 * (migration 0009; Master Plan Argus — the compliance manual builder, ADR-0007).
 *
 * All queries run client-side under the signed-in user's RLS (ADR-0004 pattern).
 * The deterministic, firm-tailored drafting (engine) runs in the browser at
 * create time; the AI enrichment goes through the `draft-manual` edge function
 * (the UI never calls Anthropic directly). Every section is reviewable: drafts
 * land `unreviewed` and require section-level human sign-off (build doc §1.4), and
 * a manual is adoptable only when every applicable section is reviewed.
 */
import { supabase } from "@/lib/supabase/client";
import {
  BROCHURE_MODEL_VERSION,
  MANUAL_MODEL_VERSION,
  diffSections,
  summarizeChanges,
  tailorBrochure,
  tailorManual,
  type DocumentType,
  type FirmProfile,
  type SectionApplicability,
  type SectionDerivation,
  type SectionDraftRow,
  type SectionReviewStatus,
  type SnapshotSection,
} from "@argus/engine";

/** The right deterministic tailor for a document kind (manual vs. 2A brochure). */
function tailorFor(docType: DocumentType, profile: FirmProfile): SectionDraftRow[] {
  return docType === "adv_2a_brochure" ? tailorBrochure(profile) : tailorManual(profile);
}

export type { DocumentType } from "@argus/engine";
export type DocumentStatus = "draft" | "in_review" | "adopted" | "archived";

export interface ComplianceDocumentRow {
  id: string;
  tenant_id: string;
  doc_type: DocumentType;
  title: string;
  status: DocumentStatus;
  firm_profile: FirmProfile;
  engine_version: string | null;
  current_version: number;
  effective_date: string | null;
  adopted_at: string | null;
  adopted_by: string | null;
  compliance_task_id: string | null;
  prepared_by: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DocumentSectionRow {
  id: string;
  tenant_id: string;
  document_id: string;
  section_key: string;
  heading: string;
  sort_order: number;
  reg_ref: string | null;
  applicability: SectionApplicability;
  body: string | null;
  source: SectionDerivation;
  is_gap: boolean;
  confidence: number | null;
  citation: string | null;
  review_status: SectionReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  notes: string | null;
}

export interface DocumentVersionRow {
  id: string;
  document_id: string;
  version_number: number;
  snapshot: SnapshotSection[];
  change_summary: string | null;
  effective_date: string | null;
  frozen_by: string | null;
  created_at: string;
}

export interface DocumentEventRow {
  id: string;
  document_id: string;
  event_type: string;
  section_key: string | null;
  from_status: string | null;
  to_status: string | null;
  note: string | null;
  actor_id: string | null;
  created_at: string;
}

const DOC_COLS =
  "id, tenant_id, doc_type, title, status, firm_profile, engine_version, current_version, effective_date, adopted_at, adopted_by, compliance_task_id, prepared_by, notes, metadata, created_at, updated_at";
const SECTION_COLS =
  "id, tenant_id, document_id, section_key, heading, sort_order, reg_ref, applicability, body, source, is_gap, confidence, citation, review_status, reviewed_by, reviewed_at, notes";

/* --------------------------------------------------------------- documents */

export async function getDocumentByType(
  tenantId: string,
  docType: DocumentType,
): Promise<ComplianceDocumentRow | null> {
  const { data, error } = await supabase
    .from("compliance_documents")
    .select(DOC_COLS)
    .eq("tenant_id", tenantId)
    .eq("doc_type", docType)
    .maybeSingle();
  if (error) throw new Error(`getDocumentByType: ${error.message}`);
  return data;
}

export async function getDocument(id: string): Promise<ComplianceDocumentRow> {
  const { data, error } = await supabase
    .from("compliance_documents")
    .select(DOC_COLS)
    .eq("id", id)
    .single();
  if (error) throw new Error(`getDocument: ${error.message}`);
  return data;
}

export async function listSections(documentId: string): Promise<DocumentSectionRow[]> {
  const { data, error } = await supabase
    .from("compliance_document_sections")
    .select(SECTION_COLS)
    .eq("document_id", documentId)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(`listSections: ${error.message}`);
  return data ?? [];
}

export async function listVersions(documentId: string): Promise<DocumentVersionRow[]> {
  const { data, error } = await supabase
    .from("compliance_document_versions")
    .select(
      "id, document_id, version_number, snapshot, change_summary, effective_date, frozen_by, created_at",
    )
    .eq("document_id", documentId)
    .order("version_number", { ascending: false });
  if (error) throw new Error(`listVersions: ${error.message}`);
  return data ?? [];
}

export async function listDocumentEvents(documentId: string): Promise<DocumentEventRow[]> {
  const { data, error } = await supabase
    .from("compliance_document_events")
    .select(
      "id, document_id, event_type, section_key, from_status, to_status, note, actor_id, created_at",
    )
    .eq("document_id", documentId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listDocumentEvents: ${error.message}`);
  return data ?? [];
}

/* --------------------------------------------------------- create / tailor */

export interface CreateManualInput {
  tenantId: string;
  profile: FirmProfile;
  userId: string;
}

/**
 * Create the firm's compliance manual and tailor every standard section to the
 * firm profile. Idempotent on (tenant, doc_type): an existing manual is returned
 * unchanged. The deterministic engine seeds a real starting draft for every
 * applicable section (the manual is never empty); "Draft with AI" enriches it.
 */
export async function createManual(input: CreateManualInput): Promise<ComplianceDocumentRow> {
  return createDocument("compliance_manual", input);
}

/**
 * Create the firm's Form ADV Part 2A brochure on the SAME substrate (ADR-0007:
 * a second `doc_type`, no migration). Items are tailored from the same firm
 * profile as the manual and several stay aligned with its policies.
 */
export async function createBrochure(input: CreateManualInput): Promise<ComplianceDocumentRow> {
  return createDocument("adv_2a_brochure", input);
}

async function createDocument(
  docType: DocumentType,
  input: CreateManualInput,
): Promise<ComplianceDocumentRow> {
  const existing = await getDocumentByType(input.tenantId, docType);
  if (existing) return existing;

  const manual = docType === "compliance_manual";
  const doc = await insertDocumentRow({
    tenant_id: input.tenantId,
    doc_type: docType,
    title: manual
      ? `${input.profile.firmName} — Compliance Manual`
      : `${input.profile.firmName} — Form ADV Part 2A Brochure`,
    status: "draft",
    firm_profile: input.profile,
    engine_version: manual ? MANUAL_MODEL_VERSION : BROCHURE_MODEL_VERSION,
    prepared_by: input.userId,
    created_by: input.userId,
  });

  const rows = tailorFor(docType, input.profile);
  await insertTailoredSections(input.tenantId, doc.id, rows);

  const applicable = rows.filter((r) => r.applicability !== "not_applicable").length;
  await recordEvent(input.tenantId, doc.id, "created", input.userId, {
    note: `${manual ? "Manual" : "Brochure"} tailored from the firm profile — ${applicable} section(s) drafted.`,
  });
  return doc;
}

/** Re-tailor the manual to an updated firm profile. Re-applies the deterministic
 * draft to sections the CCO has NOT yet reviewed/edited; reviewed sections keep
 * their human-signed text but may change applicability. */
export async function retailorManual(
  doc: ComplianceDocumentRow,
  profile: FirmProfile,
  userId: string,
): Promise<void> {
  const [existing, fresh] = [await listSections(doc.id), tailorFor(doc.doc_type, profile)];
  const byKey = new Map(existing.map((s) => [s.section_key, s]));

  for (const f of fresh) {
    const prev = byKey.get(f.sectionKey);
    const humanTouched =
      prev && (prev.review_status === "accepted" || prev.review_status === "edited");
    const patch: Record<string, unknown> = {
      applicability: f.applicability,
      // Keep human-signed prose; otherwise refresh the deterministic draft.
      ...(humanTouched
        ? {}
        : {
            body: f.body,
            source: f.source,
            is_gap: f.isGap,
            confidence: f.confidence,
            citation: f.citation,
            review_status: "unreviewed",
          }),
    };
    if (prev) {
      await supabase.from("compliance_document_sections").update(patch).eq("id", prev.id);
    } else {
      await supabase.from("compliance_document_sections").insert({
        tenant_id: doc.tenant_id,
        document_id: doc.id,
        section_key: f.sectionKey,
        heading: f.heading,
        sort_order: f.sortOrder,
        reg_ref: f.regRef,
        applicability: f.applicability,
        body: f.body,
        source: f.source,
        is_gap: f.isGap,
        confidence: f.confidence,
        citation: f.citation,
      });
    }
  }
  await supabase
    .from("compliance_documents")
    .update({ firm_profile: profile, updated_at: new Date().toISOString() })
    .eq("id", doc.id);
  await recordEvent(doc.tenant_id, doc.id, "tailored", userId, {
    note: "Re-tailored to an updated firm profile.",
  });
}

/* --------------------------------------------------------- section editing */

export async function saveSection(
  section: DocumentSectionRow,
  patch: {
    body?: string | null;
    applicability?: SectionApplicability;
    reviewStatus?: SectionReviewStatus;
    notes?: string | null;
  },
  userId: string,
): Promise<void> {
  const now = new Date().toISOString();
  const update: Record<string, unknown> = {};
  let edited = false;
  if (patch.body !== undefined) {
    update.body = patch.body;
    update.is_gap = patch.applicability === "not_applicable" ? false : !patch.body?.trim();
    edited = true;
  }
  if (patch.applicability) {
    update.applicability = patch.applicability;
  }
  if (patch.reviewStatus) {
    update.review_status = patch.reviewStatus;
    update.reviewed_by = userId;
    update.reviewed_at = now;
  } else if (edited) {
    update.review_status = "edited";
    update.reviewed_by = userId;
    update.reviewed_at = now;
  }
  if (patch.notes !== undefined) update.notes = patch.notes;

  const { error } = await supabase
    .from("compliance_document_sections")
    .update(update)
    .eq("id", section.id);
  if (error) throw new Error(`saveSection: ${error.message}`);

  await recordEvent(
    section.tenant_id,
    section.document_id,
    edited ? "section_edited" : "section_reviewed",
    userId,
    {
      sectionKey: section.section_key,
      note: patch.reviewStatus ? `marked ${patch.reviewStatus}` : undefined,
    },
  );
}

export async function acceptSection(section: DocumentSectionRow, userId: string): Promise<void> {
  await saveSection(section, { reviewStatus: "accepted" }, userId);
}

/* ----------------------------------------------------------- AI drafting */

interface DraftResponse {
  engine: string;
  configured: boolean;
  fallback?: string;
  drafts: { sectionKey: string; body: string; confidence: number; citation: string }[];
}

export interface ManualDraftResult {
  engine: string;
  configured: boolean;
  applied: number;
  fallback?: string;
}

async function tryDraftViaEdge(documentId: string): Promise<DraftResponse | null> {
  try {
    const { data, error } = (await supabase.functions.invoke<DraftResponse>("draft-manual", {
      body: { document_id: documentId },
    })) as { data: DraftResponse | null; error: { message: string } | null };
    if (error || !data || !Array.isArray(data.drafts)) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Draft (enrich) the manual's `ai` sections. Prefers the draft-manual edge
 * function (Claude when keyed); when the function is unavailable or unconfigured,
 * falls back to the in-browser deterministic engine (`tailorManual`) so "Draft
 * with AI" always completes. Re-drafting only touches sections the CCO has NOT
 * accepted/edited. Drafts land `unreviewed` for human sign-off (§1.4).
 */
export async function draftManualWithAI(
  doc: ComplianceDocumentRow,
  userId: string,
): Promise<ManualDraftResult> {
  const sections = await listSections(doc.id);
  const editable = sections.filter(
    (s) =>
      s.source === "ai" &&
      s.applicability !== "not_applicable" &&
      s.review_status !== "accepted" &&
      s.review_status !== "edited",
  );
  const editableKeys = new Set(editable.map((s) => s.section_key));

  const edge = await tryDraftViaEdge(doc.id);
  let drafts = edge?.configured ? edge.drafts.filter((d) => editableKeys.has(d.sectionKey)) : [];

  // Deterministic fallback: re-tailor the editable sections from the profile.
  let configured = !!edge?.configured && drafts.length > 0;
  if (drafts.length === 0) {
    const fresh = tailorFor(doc.doc_type, doc.firm_profile);
    const byKey = new Map(fresh.map((f) => [f.sectionKey, f]));
    drafts = editable
      .map((s) => byKey.get(s.section_key))
      .filter((f): f is NonNullable<typeof f> => !!f && !!f.body)
      .map((f) => ({
        sectionKey: f.sectionKey,
        body: f.body ?? "",
        confidence: f.confidence ?? 0.6,
        citation: f.citation ?? "Deterministic tailored draft.",
      }));
    configured = false;
  }

  let applied = 0;
  for (const d of drafts) {
    const { error } = await supabase
      .from("compliance_document_sections")
      .update({
        body: d.body,
        source: "ai",
        is_gap: false,
        confidence: d.confidence,
        citation: d.citation,
        review_status: "unreviewed",
      })
      .eq("document_id", doc.id)
      .eq("section_key", d.sectionKey);
    if (!error) applied += 1;
  }

  const modelVersion =
    doc.doc_type === "adv_2a_brochure" ? BROCHURE_MODEL_VERSION : MANUAL_MODEL_VERSION;
  const engine = configured ? (edge?.engine ?? "claude") : `deterministic/${modelVersion}`;
  await recordEvent(doc.tenant_id, doc.id, "ai_drafted", userId, {
    note: `${configured ? "Claude" : "Deterministic"} draft applied to ${applied} section(s) [${engine}].`,
  });

  const result: ManualDraftResult = { engine, configured, applied };
  if (!configured) result.fallback = edge?.fallback ?? "Drafted in-browser with the engine.";
  return result;
}

/* --------------------------------------------------- status / adoption */

export async function setManualStatus(
  doc: ComplianceDocumentRow,
  to: DocumentStatus,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from("compliance_documents")
    .update({ status: to, updated_at: new Date().toISOString() })
    .eq("id", doc.id);
  if (error) throw new Error(`setManualStatus: ${error.message}`);
  await recordEvent(doc.tenant_id, doc.id, "status_change", userId, {
    fromStatus: doc.status,
    toStatus: to,
  });
}

/**
 * Adopt the manual: freeze an immutable version snapshot with a change summary
 * (diffed against the prior version), bump the version, set status to `adopted`,
 * and stamp the effective date. Used for both first adoption and re-adoption at
 * the end of an annual review — the change log Rule 206(4)-7 expects.
 */
export async function adoptManual(
  doc: ComplianceDocumentRow,
  userId: string,
  effectiveDate?: string,
): Promise<void> {
  const sections = await listSections(doc.id);
  const snapshot: SnapshotSection[] = sections
    .filter((s) => s.applicability !== "not_applicable")
    .map((s) => ({
      sectionKey: s.section_key,
      heading: s.heading,
      body: s.body,
      applicability: s.applicability,
    }));

  const versions = await listVersions(doc.id);
  const prior = versions[0]?.snapshot ?? [];
  const changeSummary =
    versions.length === 0 ? "Initial adoption" : summarizeChanges(diffSections(prior, snapshot));

  const nextVersion = doc.current_version + 1;
  const eff = effectiveDate ?? new Date().toISOString().slice(0, 10);

  const { error: vErr } = await supabase.from("compliance_document_versions").insert({
    tenant_id: doc.tenant_id,
    document_id: doc.id,
    version_number: nextVersion,
    snapshot,
    change_summary: changeSummary,
    effective_date: eff,
    frozen_by: userId,
  });
  if (vErr) throw new Error(`adoptManual/version: ${vErr.message}`);

  const { error: dErr } = await supabase
    .from("compliance_documents")
    .update({
      status: "adopted",
      current_version: nextVersion,
      effective_date: eff,
      adopted_at: new Date().toISOString(),
      adopted_by: userId,
    })
    .eq("id", doc.id);
  if (dErr) throw new Error(`adoptManual/document: ${dErr.message}`);

  await recordEvent(doc.tenant_id, doc.id, "version_frozen", userId, {
    note: `Adopted v${nextVersion} (effective ${eff}) — ${changeSummary}.`,
  });
}

/**
 * Start the annual review (Rule 206(4)-7): link the firm's
 * `annual_compliance_review` calendar task if one exists, reset every applicable
 * section to `unreviewed` so the CCO re-examines each, and move the manual to
 * `in_review`. Re-adopting at the end freezes the next version + change log.
 */
export async function startAnnualReview(
  doc: ComplianceDocumentRow,
  userId: string,
): Promise<{ linkedTaskId: string | null }> {
  // Find an open annual-review task (requirement instantiated from the catalog).
  const { data: reqs } = await supabase
    .from("compliance_requirements")
    .select("id")
    .eq("tenant_id", doc.tenant_id)
    .eq("catalog_key", "annual_compliance_review");
  const reqIds = ((reqs ?? []) as { id: string }[]).map((r) => r.id);

  let linkedTaskId: string | null = null;
  if (reqIds.length > 0) {
    const { data: tasks } = await supabase
      .from("compliance_tasks")
      .select("id, status, due_date")
      .in("requirement_id", reqIds)
      .not("status", "in", "(completed,waived)")
      .order("due_date", { ascending: true })
      .limit(1);
    linkedTaskId = ((tasks ?? []) as { id: string }[])[0]?.id ?? null;
  }

  // Reset applicable sections to unreviewed (carry the prose forward).
  const { error: sErr } = await supabase
    .from("compliance_document_sections")
    .update({ review_status: "unreviewed", reviewed_by: null, reviewed_at: null })
    .eq("document_id", doc.id)
    .neq("applicability", "not_applicable");
  if (sErr) throw new Error(`startAnnualReview/sections: ${sErr.message}`);

  const { error: dErr } = await supabase
    .from("compliance_documents")
    .update({
      status: "in_review",
      compliance_task_id: linkedTaskId ?? doc.compliance_task_id,
      updated_at: new Date().toISOString(),
    })
    .eq("id", doc.id);
  if (dErr) throw new Error(`startAnnualReview/document: ${dErr.message}`);

  await recordEvent(doc.tenant_id, doc.id, "annual_review_started", userId, {
    note: linkedTaskId
      ? "Annual review started — linked to the calendar's annual compliance review task."
      : "Annual review started — every applicable section reset for re-review.",
  });
  return { linkedTaskId };
}

export async function recordManualExport(
  doc: ComplianceDocumentRow,
  userId: string,
): Promise<void> {
  await recordEvent(doc.tenant_id, doc.id, "exported", userId, {
    note: `${doc.doc_type === "adv_2a_brochure" ? "Form ADV Part 2A brochure" : "Compliance-manual"} package exported.`,
  });
}

/* ----------------------------------------------------------------- internal */

async function insertDocumentRow(payload: Record<string, unknown>): Promise<ComplianceDocumentRow> {
  const { data, error } = await supabase
    .from("compliance_documents")
    .insert(payload)
    .select(DOC_COLS)
    .single();
  if (error) throw new Error(`createManual: ${error.message}`);
  return data;
}

async function insertTailoredSections(
  tenantId: string,
  documentId: string,
  tailored: SectionDraftRow[],
): Promise<void> {
  const rows = tailored.map((r) => ({
    tenant_id: tenantId,
    document_id: documentId,
    section_key: r.sectionKey,
    heading: r.heading,
    sort_order: r.sortOrder,
    reg_ref: r.regRef,
    applicability: r.applicability,
    body: r.body,
    source: r.source,
    is_gap: r.isGap,
    confidence: r.confidence,
    citation: r.citation,
  }));
  const { error } = await supabase.from("compliance_document_sections").insert(rows);
  if (error) throw new Error(`createDocument/sections: ${error.message}`);
}

async function recordEvent(
  tenantId: string,
  documentId: string,
  eventType: string,
  userId: string,
  opts: {
    sectionKey?: string | undefined;
    fromStatus?: string | undefined;
    toStatus?: string | undefined;
    note?: string | undefined;
  } = {},
): Promise<void> {
  const { error } = await supabase.from("compliance_document_events").insert({
    tenant_id: tenantId,
    document_id: documentId,
    event_type: eventType,
    section_key: opts.sectionKey ?? null,
    from_status: opts.fromStatus ?? null,
    to_status: opts.toStatus ?? null,
    note: opts.note ?? null,
    actor_id: userId,
  });
  if (error) console.warn(`document_event(${eventType}): ${error.message}`);
}
